import { Suspense, lazy, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory, useParams } from 'react-router-dom';

import { Button, Flex, Typography } from 'antd';

import dayjs from 'dayjs';
import get from 'lodash.get';
import isEmpty from 'lodash.isempty';

import axios from '../../../axios';

import { capitalize } from '../../../helpers/common';
import { getConfirmationModalDescription, isGeofenceMatchedWithPhysicalGroup } from '../../../helpers/livestock';
import { hasPermission } from '../../../helpers/user';

import { DATE_FORMAT } from '../../../constants/common';
import messages from '../../../constants/messages';
import { PERMISSIONS } from '../../../constants/permissions';
import { FEATURE_LIST } from '../../../constants/subscription';

import { useUnsavedChangesPrompt } from '../../../hooks/useUnsavedChangesPrompt';

import { fetchAnimal, fetchStockTypes, updateAnimal } from '../../../redux/actions/animal';
import { fetchBreeds } from '../../../redux/actions/breed';
import { fetchFarms } from '../../../redux/actions/farm';
import { fetchLabels } from '../../../redux/actions/label';
import { fetchMeasurementTypes } from '../../../redux/actions/measurement';

import CancelSaveButtons from '../../../components/CancelSaveButtons';
import FeatureProvider from '../../../components/FeatureProvider';
import LoadingBar from '../../../components/LoadingBar';
import PrevNextButtons from '../../../components/PrevNextButtons';
import { errorToastHandler, warnToastHandler } from '../../../components/action_notifier';
import AnimalCard from '../../../components/animals/animalCard';

import AppBreadcrumb from '../../ui-components/AppBreadcrumb';
import AppDropdown from '../../ui-components/AppDropdown';

import AnimalHistory from './AnimalHistory';

const AppModal = lazy(() => import('../../../components/AppModal'));
const ConfirmationModal = lazy(() => import('../../../components/Modals/ConfirmationModal'));
const UnsavedChangesModal = lazy(() => import('../../../components/ui/UnsavedChangesModal'));

const { Title } = Typography;

function Animal(props) {
  const dispatch = useDispatch();
  const history = useHistory();

  const { id } = useParams();

  const formRef = useRef(null);

  const { animal, tableParams } = useSelector(state => state.animal);
  const user = useSelector(state => state.user);

  const [loading, setLoading] = useState(true);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [submittable, setSubmittable] = useState(false);
  const [state, setState] = useState({
    animal_edit: {},
    confirmationModalDescription: '',
    delete_modal: false,
    edit_modal: false,
    isConfirmed: false,
    isOpenConfirmationModal: false,
    selected_breed: [],
    selected_farm: [],
    selected_geofences: [],
    selected_labels: [],
    selected_sex: [],
    selected_specie: [],
    selected_type: {}
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    prepareData();
  }, [animal]);

  const navigateToThePrevPage = useCallback(() => {
    history.push(`/animals/${animal?.prev_id}`);
  }, [animal?.prev_id]);

  const navigateToTheNextPage = useCallback(() => {
    history.push(`/animals/${animal?.next_id}`);
  }, [animal?.next_id]);

  const actions = useMemo(() => {
    const availableActions = [];

    if (hasPermission([PERMISSIONS.ANIMALS.CREATE], user?.permissions)) {
      availableActions.push({
        label: (
          <Link className="app-link" to="/animals/new">
            Add livestock
          </Link>
        )
      });
    }

    if (hasPermission([PERMISSIONS.ANIMALS.UPDATE_OWN], user?.permissions)) {
      availableActions.push({
        label: (
          <Button color="default" size="small" variant="link" onClick={() => toggleModal('edit_modal')}>
            Edit livestock
          </Button>
        )
      });
    }

    if (hasPermission([PERMISSIONS.ANIMALS.DELETE_OWN], user?.permissions)) {
      availableActions.push({
        label: (
          <Button
            color="default"
            size="small"
            variant="link"
            disabled={animal?.deleted_at || !(animal?.loss_date || animal?.date_sold)}
            onClick={() => toggleModal('delete_modal')}>
            Archive livestock
          </Button>
        )
      });
    }

    return availableActions;
  }, [animal?.deleted_at, animal?.loss_date]);

  async function getAnimal() {
    const { sortField, sortOrder } = tableParams;
    await dispatch(fetchAnimal({ id, params: { sortField, sortOrder } })).unwrap();
  }

  async function fetchData() {
    try {
      await Promise.all([
        getAnimal(),
        dispatch(fetchStockTypes()).unwrap(),
        getFarms(),
        getLabels(),
        getBreeds(),
        getMeasurementTypes()
      ]);
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.FAILED_ON_FETCH_DATA);
    } finally {
      setLoading(false);
    }
  }

  async function getMeasurementTypes() {
    await dispatch(fetchMeasurementTypes()).unwrap();
  }

  async function getFarms() {
    await dispatch(fetchFarms({ with_details: true })).unwrap();
  }

  async function getLabels() {
    await dispatch(fetchLabels({ farm_ids: [animal?.farm_id] })).unwrap();
  }

  async function getBreeds() {
    await dispatch(fetchBreeds());
  }

  function prepareData() {
    if (!animal) return;

    let breed_s = {};

    const breed = get(animal, 'breed.display_name', null);
    const specie = get(animal, 'specie.name', null);

    if (breed) {
      breed_s = { value: breed.code, label: breed.display_name };
    }

    const specie_s = state.specie_opts?.filter(s => s.value === specie)[0];
    const sex_s = state.sex_opts?.filter(s => s.value === animal?.sex)[0];
    const farm_s = state.farm_opts?.filter(s => s.value === animal?.farm_id)[0];
    const leabels_s = animal?.labels?.map(x => ({ label: x.name, value: x.id, color: x.colour }));
    const fences_s = animal?.geofences?.map(x => ({ label: x.name, value: x.id }));

    setState(prevState => ({
      ...prevState,
      isConfirmed: false,
      animal: {
        ...animal,
        breed,
        specie,
        dob_at_holder: animal?.dob_at
      },
      animal_edit: {
        ...animal,
        breed,
        specie,
        dob_at_holder: dayjs(animal?.dob_at).format(DATE_FORMAT.DATE)
      },
      selected_sex: sex_s,
      selected_farm: farm_s,
      selected_specie: specie_s,
      selected_labels: leabels_s,
      selected_breed: breed_s,
      selected_geofences: fences_s,
      selected_type: {
        value: animal?.stock_type,
        label: capitalize(animal?.stock_type)
      }
    }));
  }

  function isLivestockDataValid(livestockData) {
    return (
      !isEmpty(livestockData) &&
      [livestockData.eartag_management_id, livestockData.farm_id, livestockData.specie, livestockData.sex].some(
        value => value !== undefined && value !== null && value !== ''
      )
    );
  }

  async function editAnimal(isConfirmed = false) {
    const formValues = formRef.current?.getFieldsValue();
    const livestockData = await formRef.current?.validateFields();

    if (livestockData.loss_date) {
      if (livestockData?.tag?.id) {
        errorToastHandler(messages.ANIMAL_UNLINK_TAG_REQUIRED);
        return false;
      }
    }

    livestockData.geofence_ids = [formValues.geofence_ids?.value ?? formValues.geofence_ids].filter(Boolean);
    livestockData.photo = null;

    if (state.animal_edit.photo) {
      livestockData.photo = state.animal_edit.photo;
    }

    if (!isLivestockDataValid(livestockData)) {
      errorToastHandler(messages.FILL_MANDATORY_FIELDS);
      return false;
    } else {
      // to get full livestock data
      const payloadData = {
        ...state.animal_edit,
        ...livestockData
      };
      if (!isConfirmed) {
        const isGeofenceMatched = isGeofenceMatchedWithPhysicalGroup(livestockData);
        const description = getConfirmationModalDescription(isGeofenceMatched);

        if (description) {
          setState(prevState => ({
            ...prevState,
            confirmationModalDescription: description,
            isOpenConfirmationModal: true
          }));
        } else updateAnimalReq(payloadData);
      } else updateAnimalReq(payloadData);
    }
  }

  async function updateAnimalReq(livestockData) {
    try {
      await dispatch(updateAnimal({ id, data: { ...livestockData } })).unwrap();
      await getAnimal();
      setIsFormDirty(false);
    } catch (error) {
      errorToastHandler(error || messages.FAILED_ON_UPDATE_DATA);
    }

    toggleModal('edit_modal');
  }

  function toggleModal(modal) {
    setState(prevState => ({
      ...prevState,
      animal_edit: structuredClone(prevState.animal),
      [modal]: !prevState[modal]
    }));
  }

  async function deleteAnimal() {
    if (animal?.tag?.id) {
      errorToastHandler(messages.ANIMAL_UNLINK_TAG_REQUIRED);
    } else {
      const response = await axios.delete('animals/' + id);

      if (response.status === 200) history.push('/animals');
    }
  }

  const setAnimalPhoto = photo => {
    setState(prevState => ({ ...prevState, animal_edit: { ...prevState.animal_edit, photo } }));
  };

  const { NavigationPrompt, modalProps, nextLocation, confirmed, triggerUnsavedChangesPrompt } =
    useUnsavedChangesPrompt({
      when: isFormDirty,
      onSaveAndLeave: async () => {
        if (submittable) {
          await editAnimal();

          return true;
        } else {
          warnToastHandler(messages.DATA_REQUIRED);
          formRef.current?.validateFields();

          return false;
        }
      },
      onDiscardAndLeave: () => {
        setIsFormDirty(false);
        setState(prevState => ({ ...prevState, edit_modal: false }));
      }
    });

  useEffect(() => {
    if (!isFormDirty && nextLocation) {
      history.push(nextLocation.pathname);
    }
  }, [isFormDirty, nextLocation, history]);

  const handleCancel = useCallback(() => {
    isFormDirty ? triggerUnsavedChangesPrompt() : toggleModal('edit_modal');
  }, [isFormDirty]);

  return (
    <>
      {loading ? (
        <LoadingBar fullscreen={true} />
      ) : (
        <>
          {NavigationPrompt}
          <Flex gap="small" vertical={false} justify="space-between" wrap>
            <Flex vertical={true}>
              <Title level={4}>Livestock {animal?.identifier || animal?.eartag_official_id}</Title>

              <AppBreadcrumb
                items={[
                  {
                    title: <Link to="/animals">List of Livestock</Link>
                  },
                  {
                    title: `Livestock ${animal?.identifier || animal?.eartag_official_id}`
                  }
                ]}
              />
            </Flex>
            <Flex gap="small">
              {animal?.tag && (
                <FeatureProvider name={FEATURE_LIST.TRACK_ANIMAL}>
                  <Button
                    color="primary"
                    size="large"
                    variant="solid"
                    onClick={() => history.push('/tracking/' + animal?.id)}>
                    Track Livestock
                  </Button>
                </FeatureProvider>
              )}

              {!state.edit_modal && actions?.length && <AppDropdown label="Actions" items={actions} />}

              {state.edit_modal && (
                <CancelSaveButtons disabled={!submittable} handleCancel={handleCancel} handleSave={editAnimal} />
              )}

              <PrevNextButtons
                nextId={animal?.next_id}
                prevId={animal?.prev_id}
                handlePrev={navigateToThePrevPage}
                handleNext={navigateToTheNextPage}
              />
            </Flex>
          </Flex>

          <>
            <AnimalCard
              className="mb-2"
              actionType={props.match.params.type}
              animal={state.animal_edit}
              editMode={state.edit_modal}
              formRef={formRef}
              onChange={setIsFormDirty}
              onPhotoChange={setAnimalPhoto}
              setSubmittable={setSubmittable}
            />

            <AnimalHistory animalId={id} permissions={user?.permissions} />
          </>
        </>
      )}

      <Suspense fallback={null}>
        <AppModal
          isOpen={state.delete_modal}
          confirmButtonColor="danger"
          confirmButtonText="Archive"
          title="Archive animal"
          handleCancel={() => toggleModal('delete_modal')}
          handleConfirm={deleteAnimal}>
          <div className="py-4">{messages.ARCHIVE_CONFIRMATION('animal')}</div>
        </AppModal>

        <ConfirmationModal
          isOpen={state.isOpenConfirmationModal}
          description={state.confirmationModalDescription}
          submitText={'Yes'}
          submitButtonType={'primary'}
          onCancel={() => setState({ isOpenConfirmationModal: false })}
          onSubmit={() => {
            editAnimal(true);
            setState({ isConfirmed: true, isOpenConfirmationModal: false });
          }}
        />

        <UnsavedChangesModal
          isOpen={modalProps.visible}
          handleCancel={modalProps.onCancel}
          handleConfirm={modalProps.onSave}
          handleDiscard={modalProps.onDiscard}
        />
      </Suspense>
    </>
  );
}

export default memo(Animal);
